import React, { useCallback, useState, useEffect } from 'react'
import './createroom.scss'
import Nav2 from '../../components/Nav2/Nav2'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import AddName from './AddName'
import SBNav from '../../shared/components/SideBarNav/SBNav'
import api from '../../api'
import { getOrCreateClientId } from '../../context/clientIdHelper'
import { useAuth } from 'react-oidc-context'
import { useAppUser } from '../../context/UserContext/UserContext'
import collabIcon from './collab-icon.svg'
import shareIcon from './share-icon.svg'
import { containsProfanities } from '../../shared/utils/ContainsProfanity'


const API = process.env.REACT_APP_API_URL;
const ANONYMOUS_ROOM_LIMIT = 9;
const ACCOUNT_ROOM_LIMIT = 12;
const PLACEHOLDER_NAMES = new Set(['flashroom user', 'owner']);

function hasUsableLocalName(name?: string | null) {
  const normalized = name?.trim().toLowerCase();
  return !!normalized && !PLACEHOLDER_NAMES.has(normalized);
}


function CreateRoom() {

  useEffect(() => {
    document.title = 'Create a Room'
  }, [])

  const navigate = useNavigate();
  const auth = useAuth();
  const { user: appUser, loading: appUserLoading } = useAppUser();

  const [roomName, setRoomName] = useState('');
  const [roomNameError, setRoomNameError] = useState('');
  const [shareableLinkEnabled, setShareableLinkEnabled] = useState(true);
  const [trackMembersEnabled, setTrackMembersEnabled] = useState(true);
  const [defaultMemberRole, setDefaultMemberRole] = useState<'member' | 'editor'>('member');


  const [roomCount, setRoomCount] = useState(0);
  const accountAccessToken = auth.user?.access_token;
  const hasAccountSession = auth.isAuthenticated && !!accountAccessToken;

  const flashroomName = localStorage.getItem("flashroom-accountName");


  const nameLetter: string =
    flashroomName?.trim().charAt(0).toUpperCase() ?? "";
  const roomLimit = hasAccountSession ? ACCOUNT_ROOM_LIMIT : ANONYMOUS_ROOM_LIMIT;
  const hasOwnerBypass = appUser?.plan === 'flashroom-owner';
  const shouldApplyRoomLimit = !hasOwnerBypass && !(hasAccountSession && appUserLoading);

  const getRoomCount = useCallback(async () => {
    if (auth.isLoading) return;

    try {
      if (hasAccountSession && accountAccessToken) {
        const res = await api.get('/rooms/account/owned', {
          headers: { Authorization: `Bearer ${accountAccessToken}` },
        });
        setRoomCount(res.data.rooms.length);
        return;
      }

      const clientId = localStorage.getItem("flashroom_id");
      if (!clientId) return
      const res = await api.get(`rooms/byclient/${clientId}`)

      const roomsCount = res.data.rooms.length;

      setRoomCount(roomsCount);
    } catch (err) {
    }
  }, [accountAccessToken, auth.isLoading, hasAccountSession])

  useEffect(() => {
    getRoomCount();
  }, [getRoomCount])



  const initialNameVal = () => {
    const flashroomName = localStorage.getItem("flashroom-accountName");

    if (!flashroomName) return '';

    return flashroomName;
  }


  async function createRoom(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {

      const nameOfRoom = roomName.trim();
      setRoomNameError('');

      if (!nameOfRoom) return;

      if (containsProfanities(nameOfRoom)) {
        setRoomNameError('This room name contains profanity.');
        return;
      }

      if (shouldApplyRoomLimit && roomCount >= roomLimit) {
        navigate("/rooms");
        return;
      }



      const savedOwnerName = localStorage.getItem("flashroom-accountName")?.trim();

      if (!hasAccountSession && !hasUsableLocalName(savedOwnerName)) {
        setAccountPrompt(true);
        return;
      }

      const { data: room } = hasAccountSession && accountAccessToken
        ? await api.post('/rooms/account/room', {
          roomName: nameOfRoom,
          roomType: "room",
          ownerName: savedOwnerName,
          shareableLinkEnabled,
          trackMembersEnabled,
          defaultMemberRole,
        }, {
          headers: { Authorization: `Bearer ${accountAccessToken}` },
        })
        : await axios.post(`${API}/api/rooms/room`, {
          roomName: nameOfRoom,
          roomType: "room",
          ownerClientId: getOrCreateClientId(),
          ownerName: savedOwnerName,
          creatorClientId: getOrCreateClientId(),
          shareableLinkEnabled,
          trackMembersEnabled,
          defaultMemberRole,
        });

      // if you want, store the token for later calls:
      localStorage.setItem("flashroom_roomToken", room.token);

      navigate(`/room/${room.id}`);

    } catch {
      setRoomNameError('Unable to create this room. Please try again.');
    }

  }






  const KEY_ID = 'flashroom-accountId';
  const KEY_NAME = 'flashroom-accountName';


  const [accountPrompt, setAccountPrompt] = useState(false);

  const [nav, openNav] = useState(false);


  useEffect(() => {
    if (!nav) {
      // ensure cleanup if nav is false
      document.documentElement.classList.remove("nav-blur");
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      return;
    }

    // --- lock scroll (keeps current scroll position) ---
    const scrollY = window.scrollY;

    // add blur class to <html> (we'll define CSS below)
    document.documentElement.classList.add("nav-blur");

    // lock body in place
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      // --- restore ---
      document.documentElement.classList.remove("nav-blur");

      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";

      window.scrollTo(0, scrollY);
    };
  }, [nav]);





  useEffect(() => {
    const existing = localStorage.getItem('flashroom_id')?.trim();
    const savedName = localStorage.getItem('flashroom-accountName')?.trim();

    if (!existing || !hasUsableLocalName(savedName)) setAccountPrompt(true);
  }, []);




  return (
    <div id='create-room' style={{ backgroundImage: `url('images/cbb2.png')` }}>


      <div className='nav-2'>

        <div className='nav-logo-container'>
          <img src='images/flashroom-logo-light.png' height="44px" />
          <h1>Flash<span>Room</span></h1>
        </div>

        <div className='hamburger-menu-2' onClick={() => openNav(true)}>
          <div />
          <div />
          <div />
        </div>

      </div>


      <div className={`nav-cont ${nav ? 'nav-open' : 'nav-closed'}`}>

        <div className='standard-xi-cont'

          onClick={() => openNav(false)}
        >

          <svg className="x-icon-sbn" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 111.01 111.01">
            <g id="Layer_3" data-name="Layer 3">
              <path
                className="cls-1"
                d="M232.21,289.66a10.1,10.1,0,0,1-14.31,0l-38.22-38.22-38.23,38.23a10.13,10.13,0,0,1-14.32-14.32l38.23-38.23L127.14,198.9a10.12,10.12,0,1,1,14.31-14.31l38.22,38.22,38.23-38.23a10.13,10.13,0,0,1,14.32,14.32L194,237.13l38.22,38.22A10.12,10.12,0,0,1,232.21,289.66Z"
                transform="translate(-124.17 -181.62)"
              />
            </g>
          </svg>

        </div>

        <SBNav />

      </div>


      <div id='create-room-body'>

        {accountPrompt ? (
          <AddName setAccountPrompt={setAccountPrompt} />
        ) : (
          <form id='create-room-form' onSubmit={createRoom}>

            <label className='create-room-field'>
              <span>Room Name</span>
              <input maxLength={20} type='text' value={roomName} onChange={(e) => {
                setRoomName(e.target.value);
                setRoomNameError('');
              }} placeholder='enter room name' />
              {roomNameError && <p className="create-room-error" role="alert">{roomNameError}</p>}
            </label>

            <div className='create-room-options'>
              <div className='create-room-mode'>
                <div>
                  <span>Room Mode</span>
                </div>

                <div className='create-room-mode-buttons'>
                  <button
                    type='button'
                    className={trackMembersEnabled ? 'active' : ''}
                    onClick={() => setTrackMembersEnabled(true)}
                  >
                    <div className='quick-flex'>
                      <img className='create-room-mode-icon create-room-mode-icon-collab' src={collabIcon} alt='collaboration icon' />
                      <span>Workspace</span>
                    </div>
                    {/* <p>Track members, show names, and manage collaboration.</p> */}
                  </button>

                  <button
                    type='button'
                    className={!trackMembersEnabled ? 'active' : ''}
                    onClick={() => setTrackMembersEnabled(false)}
                  >
                    <div className='quick-flex'>
                      <img className='create-room-mode-icon create-room-mode-icon-share' src={shareIcon} alt='share icon' />
                      <span>Quick Share</span>
                    </div>
                    {/* <p>Skip member tracking for a simpler sharing room.</p> */}
                  </button>
                </div>

                <p>
                  {
                    trackMembersEnabled
                      ? 'Workspace: Track members and manage access. Best for collaboration.'
                      : 'Quick Share: No member tracking. Best for simply sharing files.'
                  }
                </p>
              </div>

              <label className='create-room-toggle'>
                <div>
                  <span>Shareable Link</span>
                  <p>Allow people to join from the room URL.</p>
                </div>
                <input
                  type='checkbox'
                  checked={shareableLinkEnabled}
                  onChange={(e) => setShareableLinkEnabled(e.currentTarget.checked)}
                />
              </label>

              <div className='create-room-toggle create-room-role-toggle'>
                <div>
                  <span>Default Member Role</span>
                </div>
                <div className='create-room-role-buttons'>
                  <button
                    type='button'
                    className={defaultMemberRole === 'member' ? 'active' : ''}
                    onClick={() => setDefaultMemberRole('member')}
                  >
                    Read Only
                  </button>
                  <button
                    type='button'
                    className={defaultMemberRole === 'editor' ? 'active' : ''}
                    onClick={() => setDefaultMemberRole('editor')}
                  >
                    Editor
                  </button>
                </div>
              </div>
            </div>

            <button type='submit'>Create Room</button>


          </form>
        )


        }

      </div>


    </div>
  )
}

export default CreateRoom
